'use client';

import { PrefetchKind } from 'next/dist/client/components/router-reducer/router-reducer-types';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';

import { useAuth } from '@/providers/auth-provider';
import { CANONICAL_INBOX_PATH } from '@/utils/app-paths';

const STALE_CHECK_INTERVAL_MS = 2_000;

/**
 * Next keeps a full route prefetch for five minutes after it loads, and neither refetches it nor
 * calls `onInvalidate` when it expires, so a later click on the inbox breadcrumb waits on the
 * server. While the prefetch is fresh, `router.prefetch` only reads Next's cache, so this
 * refetches the inbox route about once every five minutes.
 */
export function KeepInboxPrefetched() {
  const router = useRouter();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const prefetchInbox = () => {
      if (document.visibilityState === 'visible') {
        router.prefetch(CANONICAL_INBOX_PATH, { kind: PrefetchKind.FULL });
      }
    };
    const intervalId = window.setInterval(prefetchInbox, STALE_CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', prefetchInbox);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', prefetchInbox);
    };
  }, [router, isAuthenticated]);

  return null;
}
